import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { applyBionicReading, removeBionicReading } from '../../lib/bionicText'
import { createAppTheme } from '../../lib/muiTheme'
import type { ColorMode, FontPreference } from '../../lib/muiTheme'
import { ThemeContext } from './ThemeContext'

const STORAGE_KEY_MODE = 'rdm-theme-mode'
const STORAGE_KEY_FONT = 'rdm-font-preference'
const STORAGE_KEY_BIONIC = 'rdm-bionic-reading'

const VALID_MODES: ColorMode[] = ['light', 'dark', 'high-contrast']
const VALID_FONTS: FontPreference[] = ['default', 'atkinson', 'opendyslexic']

function readStorage<T extends string>(key: string, fallback: T, valid: T[]): T {
  try {
    const stored = localStorage.getItem(key)
    if (stored && (valid as string[]).includes(stored)) return stored as T
  } catch {
    // localStorage may be unavailable (e.g. private browsing restrictions)
  }
  return fallback
}

function readBoolStorage(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

type Props = { children: React.ReactNode }

export const ThemeContextProvider = ({ children }: Props) => {
  const [mode, setModeSafe] = useState<ColorMode>(() => readStorage<ColorMode>(STORAGE_KEY_MODE, 'light', VALID_MODES))
  const [fontPreference, setFontPref] = useState<FontPreference>(() =>
    readStorage<FontPreference>(STORAGE_KEY_FONT, 'default', VALID_FONTS),
  )
  const [bionicReading, setBionicSafe] = useState<boolean>(() => readBoolStorage(STORAGE_KEY_BIONIC))

  const setMode = useCallback((newMode: ColorMode) => {
    setModeSafe(newMode)
    try {
      localStorage.setItem(STORAGE_KEY_MODE, newMode)
    } catch {
      // ignore
    }
  }, [])

  const setFontPreference = useCallback((font: FontPreference) => {
    setFontPref(font)
    try {
      localStorage.setItem(STORAGE_KEY_FONT, font)
    } catch {
      // ignore
    }
  }, [])

  const setBionicReading = useCallback((enabled: boolean) => {
    setBionicSafe(enabled)
    try {
      localStorage.setItem(STORAGE_KEY_BIONIC, String(enabled))
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    if (!bionicReading) {
      removeBionicReading(document.body)
      return
    }

    applyBionicReading(document.body)

    const observer = new MutationObserver(() => {
      observer.disconnect()
      applyBionicReading(document.body)
      observer.observe(document.body, { childList: true, subtree: true })
    })

    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      removeBionicReading(document.body)
    }
  }, [bionicReading])

  const theme = useMemo(() => createAppTheme(mode, fontPreference), [mode, fontPreference])

  const contextValue = useMemo(
    () => ({ mode, setMode, fontPreference, setFontPreference, bionicReading, setBionicReading }),
    [mode, setMode, fontPreference, setFontPreference, bionicReading, setBionicReading],
  )

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
